<template>
  <v-container>
    <toolbar mode="agent" :item="agent"></toolbar>
    <v-form ref="form" enctype="multipart/form-data" @submit.prevent="onSubmit" lazy-validation>
      <v-card flat :disabled="running" :loading="running">
        <template slot="progress">
          <v-progress-linear v-if="running" :value="$store.state.socket.progress.percentage" height="6" color="success"></v-progress-linear>
        </template>
        <v-card-subtitle>Automagically load data from our servers and generate predictions.</v-card-subtitle>
        <v-card-text>
          <v-autocomplete 
            :items="connections.options"
            item-text="name"
            item-value="id"
            label="Connections"
            v-model="connections.selected"
            :rules="rules" required
            clearable 
            :disabled="connections.options.length === 1"
            hint="Select a connection to use for the predictions." />
          <v-text-field v-model="identifiers"
            label="Well API/UWI"
            hint="Enter well identifiers for import in API10 format separated by commas, spaces, or newlines"
            counter clearable required
            :rules="rules">
            <template v-slot:counter>
              <span class="v-counter">{{ wells.length }} wells</span>
            </template>
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn 
                    icon 
                    v-bind="attrs"
                    v-on="on"
                    @click="loadRandomWells" 
                    v-show="connections.selected && isSuper()"
                    color="primary"
                  >
                    <v-icon>mdi-shuffle</v-icon>
                  </v-btn>
                </template>
                <span>Randomly select wells from database</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn 
                    icon 
                    v-bind="attrs"
                    v-on="on"
                    @click="loadBadWells" 
                    v-show="connections.selected && isSuper()"
                    color="primary"
                  >
                    <v-icon>mdi-alert-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Load wells from unsupported basins (for testing)</span>
              </v-tooltip>
              <shape-file-dialog 
                v-if="isWellDbConnection"
                :connection-id="connections.selected"
                @wells-found="onWellsFound"
              />
            </template>
          </v-text-field>
          <v-autocomplete :items="methods.options"
            item-text="name"
            item-value="id"
            label="Methods"
            v-model="methods.selected"
            :rules="rules" required
            clearable multiple
            hint="Select a method to use for the predictions." />
          <v-text-field v-model="predictionName"
            label="Prediction Name"
            hint="Enter a name for the prediction (e.g., 'Permian Basin Q1 2026')"
            counter clearable required
            :rules="rules" />
          
          <!-- Logs Display -->
          <v-card flat v-if="messages.length > 0">
            <v-card-text ref="logsContainer" class="logs-container">
              <pre v-if="messages.length > 0" class="logs-text">{{ logs }}</pre>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-btn depressed type="submit" color="success" :disabled="running || !formValid">Run Agent</v-btn>
          <v-btn text :disabled="running" @click="$router.push({ name: 'agentList' })">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-container>
</template>
<script>
import Toolbar from '@/components/toolbar'
import ShapeFileDialog from '@/components/ShapeFileDialog'
import Connections from '@/api/connections'
import Prediction from '@/api/predictions'
import Methods from '@/api/methods'
import Users from '@/api/users'
export default {
  name: 'Forecasting',
  props: ["agent"],
  components: {
    Toolbar,
    ShapeFileDialog,
  },
  data() {
    return {
      running: false,
      connections: {
        selected: null,
        options: []
      },
      methods: {
        selected: [],
        options: []
      },
      predictionName: null,
      identifiers: null,
      wells: [],
      messages: [],
      rules: [
        v => !!v || 'Required',
      ],
      progress: 0,
    }
  },
  computed: {
    logs() {
      return this.messages.join("\n")
    },
    formValid() {
      // Check all required fields are filled
      const hasConnection = !!this.connections.selected
      const hasName = !!this.predictionName && this.predictionName.trim().length > 0
      const hasIdentifiers = !!this.identifiers && this.wells.length > 0
      const hasMethods = this.methods.selected && this.methods.selected.length > 0
      
      return hasConnection && hasName && hasIdentifiers && hasMethods
    },
    isWellDbConnection() {
      if (!this.connections.selected || !this.connections.options) {
        return false
      }
      const connection = this.connections.options.find(c => c.id === this.connections.selected)
      return connection && connection.schema === 'welldb'
    }
  },
  watch: {
    "$store.state.socket.update": function (val) {
      this.messages.push(val)
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    "$store.state.socket.progress.percentage": function (val) {
      if (val == 100) {
        this.running = false
        this.refreshUser()
      }
    },
    identifiers: function(val) {
      if (val) {
        this.wells = val
          .replace(/-/g, '') // Remove dashes
          .split(/[\s\n,]+/) // Split by commas, spaces, or newlines
          .filter(item => item.trim() !== '') // Remove empty items
          .filter((item, index, self) => self.indexOf(item) === index) // Remove duplicates
          .filter(item => /^\d{10}$/.test(item)) // Keep only 10-digit numbers
      } else {
        this.wells = []
      }
    }
  },
  mounted() {
    Connections.list().then(response => { 
      this.connections.options = response.data.map(connection => ({
        id: connection.id,
        name: connection.name,
        schema: connection.schema,
      }))
      if (this.connections.options.length == 1) {
        this.connections.selected = this.connections.options[0].id
      }
    })
    Methods.list().then(response => {
      this.methods.options = response.data.map(method => ({
        id: method.id,
        name: method.name,
      }))
    })
  },
  methods: {
    onSubmit() {
      // No need to validate - formValid already checked before button enabled
      this.running = true
      // wells array is already computed by the watcher, no need to recalculate
      if (this.wells.length > 0) {
        this.messages = [] // Clear previous messages
        Prediction.check_wells({
          name: this.predictionName,
          connection: this.connections.selected,
          wells: this.wells,
          methods: this.methods.selected,
        }).then(response => {
          // Handle new response format with messages and approved_wells
          const messages = response.data.messages || response.data
          const approvedWells = response.data.approved_wells || []
          
          if (Array.isArray(messages)) {
            this.messages.push(...messages)
          } else {
            this.messages.push(messages)
          }
          
          this.$nextTick(() => {
            this.scrollToBottom()
          })
          
          // Only run wizard if we have approved wells
          if (approvedWells.length > 0) {
            Prediction.run_wizard({
              connection: this.connections.selected,
              wells: approvedWells, // Use approved wells instead of original wells
              methods: this.methods.selected,
              name: this.predictionName,
            }).then(_ => {
              this.notify('Running agent...')
            }).catch((error) => {
              this.running = false
              let message = error.response.data.detail || error.response.data.non_field_errors || error.response.data.wells || 'An error occurred'
              this.messages.push(`Error: ${message}`)
              this.$nextTick(() => {
                this.scrollToBottom()
              })
              this.notify(`Error: ${message}`, 'error')
            })
          } else {
            // No approved wells - don't run wizard
            this.running = false
            this.messages.push('No approved wells to process. Please ensure wells are in authorized basins.')
            this.$nextTick(() => {
              this.scrollToBottom()
            })
          }
        }).catch((error) => {
          this.running = false
          let message = error.response.data.detail || error.response.data.non_field_errors || error.response.data.wells || 'An error occurred'
          this.messages.push(`Error: ${message}`)
          this.$nextTick(() => {
            this.scrollToBottom()
          })
          this.notify(`Error: ${message}`, 'error')
        })
      } else {
        this.running = false
        this.notify('No valid identifiers found. Please enter valid 10-digit API/UWI numbers.', 'error')
      }
    },
    populateData() {
      this.predictionName = 'Test Prediction'
      this.identifiers = '4247944817 4247944973 4247945161 4247945363 4247945365 4247945364'
      this.methods.selected = this.methods.options.map(m => m.id)
    },
    onWellsFound(apis) {
      // Only set identifiers - the watcher will handle updating this.wells
      this.identifiers = apis.join(' ')
      this.notify(`Loaded ${apis.length} wells from shapefile`, 'success')
    },
    loadRandomWells() {
      if (!this.connections.selected) {
        this.notify('Please select a connection first', 'error')
        return
      }
      
      Connections.random(this.connections.selected, 10).then(response => {
        if (response.data.success && response.data.apis.length > 0) {
          this.identifiers = response.data.apis.join(' ')
        } else {
          this.notify('No wells found in database', 'warning')
        }
      }).catch(error => {
        const message = error.response && error.response.data && error.response.data.detail 
          ? error.response.data.detail 
          : 'Failed to load random wells'
        this.notify(message, 'error')
      })
    },
    loadBadWells() {
      Connections.bad_wells(this.connections.selected, 10).then(response => {
        if (response.data.success && response.data.apis.length > 0) {
          this.identifiers = response.data.apis.join(' ')
          const basins = response.data.unauthorized_basins || []
          const basinMsg = basins.length > 0 ? ` (${basins.join(', ')})` : ''
        } else {
          this.notify('No unsupported wells found in database', 'warning')
        }
      })
    },
    scrollToBottom() {
      if (this.$refs.logsContainer) {
        this.$refs.logsContainer.scrollTop = this.$refs.logsContainer.scrollHeight
      }
    },
    refreshUser() {
      // Refresh user data to update quota usage (with 1 sec delay for prediction to save)
      setTimeout(() => {
        Users.retrieve(this.user.username).then(response => {
          this.$store.commit("setUser", { user: response.data })
        })
      }, 1000)
    }
  }
}
</script>
<style>
.logs-container {
  max-height: 300px;
  overflow-y: auto;
  scroll-behavior: smooth;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.logs-text {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  border-radius: 4px;
  min-height: 100px;
}

.theme--dark .logs-text {
  background-color: rgba(255, 255, 255, 0.02);
}

.theme--dark .logs-container {
  border: 1px solid rgba(255, 255, 255, 0.12);
}
</style>